import { Icon } from "@components/library";
import { COLORS, FONTS, WINDOW_DIMENSIONS } from "@constants";
import { IconName } from "@tsTypes/icons";
import { LaunchableRequestStatus } from "@tsTypes/requests";
import { formatDateToEastern } from "@utils/dateUtils";
import { t } from "@utils/i18n";
import { LaunchableRequest, getLaunchableRequestStatus } from "@utils/requestUtils";
import styled from "styled-components";

interface Props {
  request: LaunchableRequest;
  iconPosition?: "left" | "right";
  hasUnknownLaunchDate?: boolean;
  useCompactText?: boolean;
}

export default function Status({
  request,
  iconPosition = "left",
  hasUnknownLaunchDate = false,
  useCompactText = false,
}: Props) {
  const status = getLaunchableRequestStatus(request);

  let text = "";
  let color = "";
  let background = "";
  let iconName: IconName = "" as IconName;

  switch (status) {
    case LaunchableRequestStatus.ACTIVE:
      if (request.requestable.deadline) {
        text = t("pages.request_for_startups_page.status.active", {
          date: formatDateToEastern(
            request.requestable.deadline,
            "MMM D" +
              (new Date(request.requestable.deadline).getFullYear() !== new Date().getFullYear()
                ? ", YYYY"
                : "")
          ),
        });
      } else {
        text = t("pages.request_for_startups_page.status.open");
      }
      color = COLORS.GREEN_800;
      background = COLORS.GREEN_200;
      iconName = "Accelerator";
      break;
    case LaunchableRequestStatus.COMING_SOON:
      text = hasUnknownLaunchDate
        ? "Coming soon"
        : t("pages.request_for_startups_page.status.coming_soon", {
            date: formatDateToEastern(
              request.requestable.launch_date!,
              "MMM D" +
                (new Date(request.requestable.launch_date!).getFullYear() !==
                new Date().getFullYear()
                  ? ", YYYY"
                  : "")
            ),
          });
      color = COLORS.ORANGE;
      background = COLORS.ORANGE_300;
      iconName = "Accelerator Complete";
      break;
    default:
      // Handle no date case
      if (!request.requestable.deadline && !request.requestable.launch_date) {
        if (request.enabled) {
          text = "Applications open";
          color = COLORS.GREEN_800;
          background = COLORS.GREEN_200;
          iconName = "Accelerator";
        } else {
          // Unknown status
          text = "";
        }
      } else {
        text = t("pages.request_for_startups_page.status.closed");
        color = COLORS.NEUTRAL_500;
        background = COLORS.NEUTRAL_250;
        iconName = "Accelerator Complete";
      }
  }

  let statusText = text;
  if (useCompactText) {
    if (statusText.toLowerCase().includes("applications closed")) {
      statusText = "Closed";
    } else if (statusText.toLowerCase().includes("applications open")) {
      statusText = "Open";
    }
  }

  return (
    <Container iconPosition={iconPosition} color={color}>
      {Number(iconName?.length) > 0 && (
        <IconContainer background={background}>
          <Icon name={iconName} size="sm" color={color} />
        </IconContainer>
      )}
      {statusText}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: ${({ iconPosition }) => (iconPosition === "left" ? "row" : "row-reverse")};
  align-items: center;
  gap: 8px;
  color: ${({ color }) => color};
  ${FONTS.SEMIBOLD_3}
  white-space: nowrap;
  @media (max-width: ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY}px) {
    text-align: left;
    float: left;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 26px;
  height: 26px;

  border-radius: 100%;
  background: ${({ background }) => background};
`;
